import {
  ArrowUpRightFromSquareRegular,
  CircleDotIcon,
  HashRegularIcon,
  ListTimelineRegularIcon,
} from "@elixir/icons";
import { convertPatientProfileToParchiPatient } from "@elixir/utils";
import { Drawer } from "antd";
import {
  ActivityLogCardUi,
  LoadingStateUi,
  PatientDetailsComponentUi,
} from "ui";
import useGetAppointmentActivityFromFS, {
  useGetVendorDataForAppointments,
} from "../../apis/tryst/use-get-appointment-logs-styles";
import useQueueStore from "../../stores/queue-store";
import { VendorMetaType } from "@elixir/types";
import Spinner from "../../atoms/spinner";

const AppointmentLogsDrawer = () => {
  const setShowAppointmentDetailsDrawer = useQueueStore(
    (state) => state.setShowAppointmentDetailsDrawer
  );
  const showAppointmentDetailsDrawer = useQueueStore(
    (state) => state.showAppointmentDetailsDrawer
  );

  const selectedAppointment = useQueueStore(
    (state) => state.selectedAppointment
  );
  const setSelectedAppointment = useQueueStore(
    (state) => state.setSelectedAppointment
  );

  if (!selectedAppointment) return null;

  return (
    <Drawer
      bodyStyle={{ padding: 0 }}
      closable={false}
      placement={"right"}
      onClose={() => {
        setShowAppointmentDetailsDrawer(false);
        setSelectedAppointment(undefined);
      }}
      open={showAppointmentDetailsDrawer}
    >
      {selectedAppointment ? (
        <PatientDetailsComponentUi
          patient={convertPatientProfileToParchiPatient(selectedAppointment)}
        />
      ) : null}
      <div className="dr-py-10 dr-px-16 dr-bg-darwin-touch-neutral-50 dr-flex dr-flex-row dr-text-darwin-touch-neutral-1000 dr-items-center dr-space-x-16">
        <ListTimelineRegularIcon className="dr-w-16 dr-h-16" />
        <div className="HeadlineBold">Appointment Details</div>
      </div>

      <AppointmentActivityList aid={selectedAppointment.aid} />
    </Drawer>
  );
};

export function SimpleTable({ aid }: { aid: string }) {
  const {
    data: vendorMeta,
    isLoading,
    error,
  } = useGetVendorDataForAppointments(aid);

  if (isLoading) {
    return (
      <div className="dr-p-16 dr-max-w-500">
        <div className="flex items-center gap-7">
          <Spinner />
        </div>
      </div>
    );
  }
  if (error || !vendorMeta?.meta?.vendor_meta.length) return null;

  return (
    <div className="dr-px-12 dr-py-4 dr-border dr-border-doctor-darwin-neutral-200 dr-rounded-8 dr-divide-doctor-darwin-neutral-200 dr-divide-y-1 dr-flex dr-flex-col">
      {vendorMeta.meta?.vendor_meta.map((row) =>
        row.type === VendorMetaType.TEXT ? (
          <div
            key={row.id}
            className="dr-flex dr-justify-between dr-items-center dr-py-8"
          >
            <div className="BodyRegular dr-flex dr-space-x-8 dr-items-center">
              <HashRegularIcon
                className={"dr-h-14 dr-w-14 dr-text-doctor-darwin-neutral-800"}
              />
              <p>{row.name}</p>
            </div>
            <p className="BodyBold">{row.value}</p>
          </div>
        ) : (
          <a
            href={row.value}
            key={row.id}
            target="_blank"
            rel="noreferrer"
            className="dr-flex dr-justify-between dr-items-center"
          >
            <div className="dr-flex dr-justify-between dr-items-center dr-py-8">
              <div className="BodyRegular dr-items-center dr-flex dr-space-x-8">
                <ArrowUpRightFromSquareRegular
                  className={
                    "dr-h-14 dr-w-14 dr-text-doctor-darwin-neutral-800"
                  }
                />
                <p>{row.name}</p>
              </div>
              <div></div>
            </div>
          </a>
        )
      )}
    </div>
  );
}

export function AppointmentActivityList({ aid }: { aid: string }) {
  const { logStyleData, isLogDataLoading } =
    useGetAppointmentActivityFromFS(aid); // FS call

  const noLogs = !isLogDataLoading && !logStyleData.length;

  return (
    <div className="dr-flex dr-flex-col dr-p-16 dr-space-y-16 dr-overflow-auto dr-max-w-500 dr-min-h-[80vh]">
      <SimpleTable aid={aid} />

      {noLogs && (
        <div className="dr-flex dr-h-full dr-w-full dr-flex-col dr-items-center dr-justify-center dr-space-y-16 dr-select-none dr-mt-32">
          <svg
            width="42"
            height="48"
            className="fill-neutral-3"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 384 512"
          >
            <path d="M369.9,97.9,286,14A48,48,0,0,0,252.1-.1H48A48.16,48.16,0,0,0,0,48V464a48,48,0,0,0,48,48H336a48,48,0,0,0,48-48V131.9A48.23,48.23,0,0,0,369.9,97.9Zm-22.6,22.7a15.73,15.73,0,0,1,4.2,7.4H256V32.5a15.73,15.73,0,0,1,7.4,4.2ZM336,480H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32H224V136a23.94,23.94,0,0,0,24,24H352V464A16,16,0,0,1,336,480ZM262.71,369.05l-5.66,5.66a12,12,0,0,1-17,0L192,326.63l-48.08,48.08a12,12,0,0,1-17,0l-5.66-5.66a12,12,0,0,1,0-17L169.37,304l-48.08-48.08a12,12,0,0,1,0-17l5.66-5.66a12,12,0,0,1,17,0L192,281.37l48.08-48.08a12,12,0,0,1,17,0l5.66,5.66a12,12,0,0,1,0,17L214.63,304l48.08,48.08A12,12,0,0,1,262.71,369.05Z" />
          </svg>
          <div>
            <p className="text-neutral-3">No Appointment Activity Found</p>
          </div>
        </div>
      )}

      {!isLogDataLoading && Boolean(logStyleData.length) && (
        <div className="dr-px-16 dr-relative">
          <div className="dr-h-full dr-w-1 dr-absolute dr-left-24 dr-top-40 dr-border-dashed dr-border-1 dr-border-darwin-touch-neutral-50 -dr-z-1" />
          {logStyleData.map((log, index) => {
            const { time, dayByMonth } = log.styled;
            return (
              <div
                key={log.id}
                className={index === logStyleData.length - 1 ? "" : "dr-pb-24"}
              >
                <div className="dr-flex dr-flex-row dr-items-center dr-space-x-12">
                  <div className="dr-flex dr-flex-row dr-space-x-12 dr-items-center">
                    <CircleDotIcon className="dr-text-darwin-touch-neutral-500" />
                    <div className="dr-flex dr-flex-col CalloutRegular dr-text-darwin-touch-neutral-600">
                      <div>{time}</div>
                      <div>{dayByMonth}</div>
                    </div>
                  </div>
                  <div className="dr-flex-1">
                    <ActivityLogCardUi {...log.styled} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isLogDataLoading && (
        <LoadingStateUi className="dr-flex dr-h-screen dr-flex-col dr-space-y-24 dr-p-16 dr-max-w-500">
          {/* <div className="dr-h-60 dr-w-full dr-bg-darwin-touch-neutral-100 dr-rounded-8" /> */}
          <div className="dr-h-60 dr-w-full dr-bg-darwin-touch-neutral-100 dr-rounded-8" />
          <div className="dr-h-60 dr-w-full dr-bg-darwin-touch-neutral-100 dr-rounded-8" />
          <div className="dr-h-60 dr-w-full dr-bg-darwin-touch-neutral-100 dr-rounded-8" />
        </LoadingStateUi>
      )}
    </div>
  );
}

export default AppointmentLogsDrawer;
